import type { Application } from 'pixi.js-legacy';
import { Sprite, Texture } from 'pixi.js-legacy';
import { GameState } from './game-state';
import { GridRenderer } from '../renderer/grid-renderer';
import { HUD } from '../renderer/hud';
import { InputHandler } from '../renderer/input-handler';
import { saveBestScore, loadBestScore } from './storage';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../renderer/app';
import { SoundManager } from './sound-manager';

/**
 * Check if winter theme is enabled via URL parameter
 */
function isWinterThemeEnabled(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('winter') === '1';
}

/**
 * Get background image path based on theme
 */
function getBackgroundImage(): string {
  const isWinter = isWinterThemeEnabled();
  return isWinter ? './images/background_winter.jpg' : './images/background.jpg';
}

/**
 * Main game controller that coordinates all components
 */
export class GameController {
  private app: Application;
  private gameState: GameState;
  private gridRenderer: GridRenderer;
  private hud: HUD;
  private wasAnimating: boolean = false;
  private gameOverSoundPlayed: boolean = false;

  constructor(app: Application) {
    this.app = app;

    // Create and add background sprite
    this.setupBackground();

    // Load best score from storage
    const bestScore = loadBestScore();

    // Initialize game state
    this.gameState = new GameState(Date.now(), bestScore);

    // Initialize renderers
    this.gridRenderer = new GridRenderer();
    this.hud = new HUD();

    // Add to stage (after background)
    this.app.stage.addChild(this.gridRenderer.getContainer());
    this.app.stage.addChild(this.hud.getContainer());

    // Setup HUD callbacks
    this.hud.setCallbacks({
      onUndo: () => this.handleUndo(),
      onRestart: () => this.handleRestart(),
      onContinue: () => this.handleContinue(),
    });

    // Initialize input handler (no need to store reference)
    new InputHandler(
      this.app,
      this.gridRenderer,
      this.gameState,
      (removedTiles) => this.handleMove(removedTiles)
    );

    // Initial render
    this.render();

    // Start game loop
    this.app.ticker.add(() => this.update());
  }

  /**
   * Setup background image
   */
  private setupBackground(): void {
    const backgroundTexture = Texture.from(getBackgroundImage());
    const background = new Sprite(backgroundTexture);

    // Scale to cover the entire logical game area
    const scaleX = LOGICAL_WIDTH / backgroundTexture.width;
    const scaleY = LOGICAL_HEIGHT / backgroundTexture.height;
    const scale = Math.max(scaleX, scaleY); // Use max to cover entire area

    background.scale.set(scale, scale);

    // Center the background
    background.x = (LOGICAL_WIDTH - background.width) / 2;
    background.y = (LOGICAL_HEIGHT - background.height) / 2;

    // Add as first child (bottom layer)
    this.app.stage.addChild(background);
  }

  /**
   * Handle move completion
   */
  private handleMove(
    removedTiles: Array<{ x: number; y: number }> | null
  ): void {
    if (removedTiles && removedTiles.length > 0) {
      // Play swoosh sound when tiles are removed
      SoundManager.play('swoosh');

      // Get board state before gravity for correct tile colors
      const boardBefore = this.gameState.getBoard();

      // Clone the board before applying gravity (for animation tracking)
      const boardBeforeClone = JSON.parse(JSON.stringify(boardBefore));

      // Apply gravity to get the new positions
      this.gameState.applyGravity();
      const boardAfter = this.gameState.getBoard();

      // Start animations (crumble + tile movement)
      this.gridRenderer.animateTileRemoval(
        removedTiles,
        boardBeforeClone,
        boardAfter
      );

      // Mark that we're animating
      this.wasAnimating = true;

      // Update score immediately (tiles are already marked as removed)
      this.render();

      // Hide continue button and no moves message during animations (there are moves now)
      this.hud.setContinueVisible(false);
      this.hud.setNoMovesVisible(false);
    } else {
      this.checkGameState();
    }
  }

  /**
   * Handle undo button
   */
  private handleUndo(): void {
    if (this.gameState.undo()) {
      this.gridRenderer.clearAnimations();
      this.hud.setNoMovesVisible(false);
      this.gameOverSoundPlayed = false; // Reset gameover sound flag
      this.render();
      this.checkGameState();
    }
  }

  /**
   * Handle restart button
   */
  private handleRestart(): void {
    this.gridRenderer.clearAnimations();
    this.hud.setNoMovesVisible(false);
    this.gameOverSoundPlayed = false; // Reset gameover sound flag
    this.gameState.restart(Date.now());
    this.render();
    this.checkGameState();
  }

  /**
   * Handle continue button (after reaching target)
   */
  private handleContinue(): void {
    this.gridRenderer.clearAnimations();
    this.gameOverSoundPlayed = false; // Reset gameover sound flag
    this.gameState.continue(Date.now());
    this.render();
    this.checkGameState();
  }

  /**
   * Check game state after move and update UI accordingly
   */
  private checkGameState(): void {
    const isGridEmpty = this.gameState.isGridEmpty();
    const hasMovesRemaining = this.gameState.hasMovesRemaining();

    // Save best score
    saveBestScore(this.gameState.getBestScore());

    // Show continue button only when grid is completely empty
    if (isGridEmpty) {
      this.hud.setContinueVisible(true);
      this.hud.setNoMovesVisible(false);
    } else {
      this.hud.setContinueVisible(false);

      // Show "no moves" message when grid has tiles but no valid moves
      if (!hasMovesRemaining) {
        this.hud.setNoMovesVisible(true);

        // Play gameover sound once
        if (!this.gameOverSoundPlayed) {
          SoundManager.play('gameover');
          this.gameOverSoundPlayed = true;
        }
      } else {
        this.hud.setNoMovesVisible(false);
        // Reset gameover sound flag when moves are available again
        this.gameOverSoundPlayed = false;
      }
    }

    // Update HUD
    this.hud.setUndoEnabled(this.gameState.canUndo());
  }

  /**
   * Render the current game state
   */
  private render(): void {
    const board = this.gameState.getBoard();

    // Render grid
    this.gridRenderer.render(board);

    // Update HUD
    this.hud.updateScore(this.gameState.getScore());
    this.hud.updateBest(this.gameState.getBestScore());
    this.hud.setUndoEnabled(this.gameState.canUndo());
  }

  /**
   * Update loop for animations
   */
  private update(): void {
    const deltaTime = this.app.ticker.deltaTime;

    // Update all animations (particles and tile movement)
    this.gridRenderer.updateAnimations(deltaTime);

    const isAnimating = this.gridRenderer.isAnimating();

    // Check game state only once when animations finish
    if (this.wasAnimating && !isAnimating) {
      this.checkGameState();
    }

    this.wasAnimating = isAnimating;
  }
}
